import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Message } from 'mage-i18n';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  IllustrationBank,
  SpacerVertical,
  SwitchCheckbox,
  Typography,
  Link,
} from '@klarna/bubble-ui';
import { Row, Column } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { TermsAndConditionsLink } from '@/components';
import {
  getMerchantCountryTranslated,
  hasProductsWithNoPrice,
  merchantRegion,
} from '@/selectors/merchant';

import ProductPrices from './ProductPrices';
import { APP_PATHS } from '@/constants';
import { CY_ADDITIONAL_PRODUCT_PAGE_IDS } from './constants';

const tKeyTerms = 'products-fe.components.terms_and_conditions_confirmation';

const ProductPricesBox = ({
  isTocAccepted,
  merchantCountryTranslated,
  openPricing,
  products,
  referrer,
  region,
  selectedProductId,
  showPriceNotAvailableDisclaimer,
  selectedProductOptionIds = [],
  termsAndConditionsLink = '',
  onChange,
}) => {
  return (
    <Row>
      <Column columns={{ tablet: 3 }} display={{ mobile: 'none' }} alignItems="flex-end">
        <IllustrationBank />
      </Column>
      <Column columns={{ tablet: 9 }}>
        {products && (
          <>
            <Typography textToken="text-style/headings/titles/bold/tertiary">
              <Message
                path={`${tKeyTerms}.pricing`}
                params={{ country: merchantCountryTranslated }}
              />
            </Typography>
            <SpacerVertical spaceToken="space/300" />
            <ProductPrices products={products} />
            {showPriceNotAvailableDisclaimer && (
              <>
                <SpacerVertical spaceToken="space/300" />
                <Typography textToken="text-style/text/labels/default/regular">
                  <Message path={`${tKeyTerms}.product_not_available_in_your_country`} />
                </Typography>
              </>
            )}
          </>
        )}
        {region !== 'us' && (
          <>
            <SpacerVertical spaceToken="space/150" />
            <Link
              textToken="text-style/text/paragraphs/body/medium"
              onPress={e => {
                e.preventDefault();
                openPricing(referrer);
              }}
            >
              <Message path={`products-fe.general.pricing_link_${region}`} />
            </Link>
          </>
        )}
        <SpacerVertical spaceToken="space/300" />
        <Row>
          <Column>
            <SwitchCheckbox
              onChange={onChange}
              checked={isTocAccepted}
              {...generateCyElemId(CY_ADDITIONAL_PRODUCT_PAGE_IDS.CHECKBOX, window)}
            >
              <TermsAndConditionsLink
                tocLink={termsAndConditionsLink}
                textBeforeLink={<Message path={`${tKeyTerms}.before_link`} />}
                linkText={<Message path={`${tKeyTerms}.link_text`} />}
                textAfterLink={
                  products.length > 1 ? (
                    <Message path={`${tKeyTerms}.after_link_more_than_one_product`} />
                  ) : (
                    <Message path={`${tKeyTerms}.after_link_one_product`} />
                  )
                }
              />
            </SwitchCheckbox>
          </Column>
        </Row>
        <SpacerVertical spaceToken="space/300" />
      </Column>
    </Row>
  );
};

ProductPricesBox.propTypes = {
  history: PropTypes.object.isRequired, // eslint-disable-line
  isTocAccepted: PropTypes.bool.isRequired,
  merchantCountryTranslated: PropTypes.string.isRequired,
  openPricing: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  referrer: PropTypes.string.isRequired,
  region: PropTypes.oneOf(['eu', 'us']).isRequired,
  selectedProductId: PropTypes.string.isRequired,
  showPriceNotAvailableDisclaimer: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  selectedProductOptionIds: PropTypes.array,
  termsAndConditionsLink: PropTypes.string,
};

export default connect(
  state => ({
    region: merchantRegion(state),
    showPriceNotAvailableDisclaimer: hasProductsWithNoPrice(state),
    merchantCountryTranslated: getMerchantCountryTranslated(state),
  }),
  {},
  (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    openPricing: referrer =>
      ownProps.history.push({ pathname: APP_PATHS.PRICING_PATH, state: { referrer } }),
  }),
)(ProductPricesBox);
